import { useApp, useBehavior, useInputPhone, useTriggerAction, updateWidgetValue, useAppDispatch, camelCaseToKebabCase, getFieldName } from '@libs/core';
import { useConsumerInput } from '../../hooks/useInputs';
import { THandleChangeInputPhone, TWidgetProps } from '@libs/types';
import React, { useEffect, useRef, useState } from 'react';
import { useGetTelegramPhoneNumber } from '../../hooks/useTelegramMiniApp';
import { TConsumerInputPhoneData, TConsumerItem, TInputPhoneMessages } from '../../types/data';
import { TInputOption } from '../../types/options';
import { InputPhoneView } from './InputPhoneView';

/**
 * Phone input widget driven by server-provided configuration. Owns all behavior:
 * input/behavior/trigger hooks, country-selection state, prefix normalization, and
 * the change/select handlers. The overlay rendering (country modal + error popup)
 * lives in InputPhoneView; this widget only supplies the callbacks and state it needs.
 */
const InputPhoneWidget = ({ widget }: TWidgetProps<TConsumerInputPhoneData, TInputOption>) => {
    const phoneRef = useRef<HTMLInputElement | null>(null);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const messages = (data?.messages ?? {}) as TInputPhoneMessages;
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal, setPopup } = useApp();
    const { value, isFocused, isError, errorMessage, label, helperText, handleFocus, handleBlur } = useConsumerInput(widget, {
        isHidden,
        isDisabled,
        triggerActionsByType,
        setModal,
    });
    const { iso2, handleChangeInputPhone } = useInputPhone(widget);
    const [itemId, setItemId] = useState(data.items?.find((i) => i.countryIso === iso2)?.id ?? widget?.data?.value);
    const hasItems = Array.isArray(data.items) && data.items.length > 0;
    const dispatch = useAppDispatch();

    const selectedItem = data.items?.find((i) => i.id === itemId) ?? data.items?.find((i) => i.countryIso === iso2);
    const prefixDigits = String((selectedItem?.value as string) ?? '').replace(/\D/g, '');

    const handleChangePhone: THandleChangeInputPhone = (value, countryData, event, formattedValue) => {
        const clearFormattedValue = formattedValue?.replace(/\D/g, '') ?? '';
        const prefix = ((countryData?.dialCode as string) ?? '').replace(/\D/g, '') || prefixDigits;
        if (prefix && clearFormattedValue === prefix) {
            dispatch(updateWidgetValue({ value: '', uid: widget.uid }));
        } else {
            handleChangeInputPhone(value, countryData, event, formattedValue);
        }
    };

    const didInitPrefixCheck = useRef(false);
    useEffect(() => {
        if (didInitPrefixCheck.current) {
            return;
        }
        didInitPrefixCheck.current = true;
        const clearValue = String(value ?? '').replace(/\D/g, '');
        if (prefixDigits && clearValue === prefixDigits) {
            dispatch(updateWidgetValue({ value: '', uid: widget.uid }));
        }
    }, [dispatch, prefixDigits, value, widget.uid]);

    const displayValue = value || prefixDigits;

    useGetTelegramPhoneNumber(widget, handleChangeInputPhone);

    const onSelectItem = (_wid: string, item: TConsumerItem) => {
        if (hasItems && item) {
            setModal(null);
            setItemId(item.id);
            handleChangeInputPhone?.(
                item.metaText ?? '',
                {
                    countryCode: item.icon ?? '',
                    regex: item.regex,
                    isChangeCountry: true,
                },
                undefined,
                item.metaText,
            );
            //@ts-expect-error
            // https://github.com/bl00mber/react-phone-input-2/issues/187#issuecomment-799988994
            phoneRef?.current?.numberInputRef?.focus?.();
        }
    };

    return (
        <InputPhoneView
            widget={widget}
            uid={widget.uid}
            label={label}
            helperText={helperText}
            isError={isError}
            errorMessage={errorMessage}
            isFocused={isFocused}
            isDisabled={isDisabled}
            isHidden={isHidden}
            margin={options.margin}
            iso2={iso2}
            hasItems={hasItems}
            items={data.items}
            displayValue={displayValue}
            value={value}
            selectedItemId={itemId as string | undefined}
            selectPrefixHeaderText={messages.headerForSelectPhonePrefix}
            errorDescriptionTemplate={messages.errorMessageDescription}
            retryButtonLabel={messages.buttonFirstLabelLabel}
            retryButtonIcon={data.buttonFirstLabelIcon}
            editButtonLabel={messages.buttonSecondLabelLabel}
            editButtonIcon={data.buttonSecondLabelIcon}
            phoneRef={phoneRef}
            autoFocus={options.onFocus || false}
            name={getFieldName(options.autofill, 'tel')}
            autoComplete={camelCaseToKebabCase(options.autofill ?? 'tel')}
            required={data.mandatory}
            maxLength={options.maxLength}
            tabIndex={data.tabIndex}
            setModal={setModal}
            setPopup={setPopup}
            onSelectItem={onSelectItem}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChangePhone}
        />
    );
};

export { InputPhoneWidget };
